/// <amd-module name="Dashboards/DashboardView/src/redux/sagas/SagaHelpers" />
import { Channel, effects } from "redux-saga";
import { Action } from "redux/Utils/ActionHelper";
import { ErrorActions } from "../actions/ErrorActions";

/**
 * Wraps the given saga so any errors it produces don't reach the root saga which would cause a catastrophic failure.
 * Use for sagas that get forked (effects.fork, effects.takeEvery, effects.takeLeading, etcetera).
 */
export function errorHandledSaga(
    saga: (...args: unknown[]) => IterableIterator<effects.Effect>
): (...args: unknown[]) => IterableIterator<effects.Effect> {
    // The "any number of args" type definition for 'effects.call' requires 7 args, then the rest
    return function*(
        arg1: unknown,
        arg2: unknown,
        arg3: unknown,
        arg4: unknown,
        arg5: unknown,
        arg6: unknown,
        ...rest
    ): IterableIterator<effects.Effect> {
        try {
            yield effects.call(saga, arg1, arg2, arg3, arg4, arg5, arg6, rest);
        } catch (e) {
            yield effects.put(ErrorActions.notifySagaFailure(e));
        }
    };
}

/** Effects not provided "out-of-box" in redux-saga. */
export namespace customEffects {
    /** Listens for the given action pattern and runs the given worker the first time the action pattern is received. */
    export function takeOnce(pattern: string, worker: effects.HelperFunc0<Action<any>>): effects.ForkEffect {
        return effects.fork(function*() {
            const action: Action<any> = (yield effects.take(pattern)) as any;
            yield effects.call(worker, action);
        });
    }

    /**
     * Listens for the first instance of the given action pattern and runs the given worker.
     * After the worker finishes, if while it was running, more instances of the given pattern were dispatched,
     * the worker is run again using the latest action payload dispatched.
     */
    export function takeLeadingAndLatest(pattern: string, worker: effects.HelperFunc0<Action<any>>): effects.ForkEffect {
        return effects.fork(function*() {
            // Buffer actions
            const channel: Channel<any> = (yield effects.actionChannel(pattern)) as any;
            try {
                while (true) {
                    let action: Action<any> = (yield effects.take(channel)) as any;

                    // Clear any queued actions
                    const bufferedActions: Action<any>[] = (yield effects.flush(channel)) as any;

                    // Use the latest action only
                    if (bufferedActions && bufferedActions.length > 0) {
                        action = bufferedActions[bufferedActions.length - 1];
                    }

                    yield effects.call(worker, action);
                }
            } finally {
                if (effects.cancelled()) {
                    channel.close();
                }
            }
        });
    }
}
